import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  Search,
  FilterList,
  ShoppingCart,
  Favorite,
  Share,
  ShoppingCartOutlined,
  PersonOutline,
  Settings,
  Logout,
  AccountCircle,
  FavoriteBorder,
  ListAlt,
} from '@mui/icons-material';
import Footer from '../components/Footer';
import logo from '../assets/logo0.png';

const Marketplace = () => {
  // Add these state variables
  const [anchorEl, setAnchorEl] = useState(null);
  const [cartAnchorEl, setCartAnchorEl] = useState(null);
  const [cartItems, setCartItems] = useState([]); // For cart count
  const [page, setPage] = useState(1);
  const [productsPerPage] = useState(20);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCartMenuOpen = (event) => {
    setCartAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCartAnchorEl(null);
  };

  // Categories
  const categories = [
    { id: 'pc', name: 'PC Hardware', icon: 'computer' },
    { id: 'phones', name: 'Smartphones', icon: 'smartphone' },
    { id: 'tv', name: 'TVs & Entertainment', icon: 'tv' },
    { id: 'gaming', name: 'Gaming', icon: 'sports_esports' },
    { id: 'accessories', name: 'Accessories', icon: 'headset' },
    { id: 'networking', name: 'Networking', icon: 'router' }
  ];

  // Extended products data
  const products = [
    // PC Hardware
    {
      id: 1,
      name: 'Gaming PC Bundle',
      description: 'High-performance gaming PC with RGB lighting',
      price: 1299.99,
      rating: 4.8,
      reviews: 156,
      image: 'https://www.pcworld.com/wp-content/uploads/2024/03/600-Euro-Gaming-PC-1.jpg?resize=1024%2C576&quality=50&strip=all',
      category: 'PC Hardware',
      tags: ['Gaming', 'RGB', 'High Performance'],
      specs: {
        processor: 'Intel i9 12900K',
        gpu: 'RTX 4080',
        ram: '32GB DDR5',
        storage: '2TB NVMe SSD'
      }
    },
    {
      id: 2,
      name: 'Professional Workstation',
      description: 'Powerful workstation for content creators',
      price: 2499.99,
      rating: 4.9,
      reviews: 89,
      image: 'https://i.dell.com/sites/csimages/App-Merchandizing_Images/all/prod-369400-workstation-desktop-precision-3660-5860-t7960-800x620.png',
      category: 'PC Hardware',
      tags: ['Workstation', 'Professional', '4K'],
      specs: {
        processor: 'AMD Threadripper',
        gpu: 'RTX 4090',
        ram: '128GB DDR5',
        storage: '4TB NVMe RAID'
      }
    },

    // Smartphones
    {
      id: 3,
      name: 'Ultimate Smartphone Pro',
      description: 'Latest flagship smartphone with advanced camera system',
      price: 999.99,
      rating: 4.7,
      reviews: 342,
      image: 'https://techland.tn/uploads/media/s6bYGAP3g7RtBiVEl07WvwZtUfzA6W7qPQmXdwoC.jpg',
      category: 'Smartphones',
      tags: ['5G', 'Pro Camera', 'AMOLED'],
      specs: {
        screen: '6.7" AMOLED 120Hz',
        camera: '108MP Main + 48MP Ultra',
        battery: '5000mAh',
        storage: '512GB'
      }
    },
    {
      id: 4,
      name: 'Budget Smartphone Plus',
      description: 'Great value smartphone with premium features',
      price: 399.99,
      rating: 4.5,
      reviews: 567,
      image: 'https://via.placeholder.com/300x200',
      category: 'Smartphones',
      tags: ['Value', '5G', 'Long Battery'],
      specs: {
        screen: '6.5" LCD 90Hz',
        camera: '64MP Main + 12MP Ultra',
        battery: '5000mAh',
        storage: '128GB'
      }
    },

    // TVs & Entertainment
    {
      id: 5,
      name: '65" 4K OLED Smart TV',
      description: 'Premium OLED TV with stunning picture quality',
      price: 1799.99,
      rating: 4.9,
      reviews: 234,
      image: 'https://via.placeholder.com/300x200',
      category: 'TVs & Entertainment',
      tags: ['OLED', '4K', 'Smart TV'],
      specs: {
        resolution: '4K UHD',
        hdr: 'Dolby Vision',
        sound: 'Dolby Atmos',
        refresh: '120Hz'
      }
    },
    {
      id: 6,
      name: '55" QLED Gaming TV',
      description: 'Optimized for gaming with low latency',
      price: 899.99,
      rating: 4.6,
      reviews: 178,
      image: 'https://via.placeholder.com/300x200',
      category: 'TVs & Entertainment',
      tags: ['Gaming', 'QLED', '4K'],
      specs: {
        resolution: '4K UHD',
        refresh: '144Hz',
        latency: '< 5ms',
        vrr: 'FreeSync Premium'
      }
    },

    // Gaming
    {
      id: 7,
      name: 'Pro Gaming Console',
      description: 'Next-gen gaming console with ray tracing',
      price: 499.99,
      rating: 4.8,
      reviews: 892,
      image: 'https://via.placeholder.com/300x200',
      category: 'Gaming',
      tags: ['Console', 'Ray Tracing', '4K'],
      specs: {
        resolution: '4K',
        storage: '1TB SSD',
        fps: '120fps',
        features: 'Ray Tracing'
      }
    },

    // Accessories
    {
      id: 8,
      name: 'Wireless Gaming Headset',
      description: 'Premium gaming headset with spatial audio',
      price: 199.99,
      rating: 4.7,
      reviews: 445,
      image: 'https://via.placeholder.com/300x200',
      category: 'Accessories',
      tags: ['Wireless', 'Gaming', 'Audio'],
      specs: {
        battery: '30 hours',
        audio: '7.1 Surround',
        connection: 'Bluetooth 5.0',
        features: 'Noise Cancelling'
      }
    },

    // Networking
    {
      id: 9,
      name: 'Mesh WiFi System Pro',
      description: 'Whole-home mesh WiFi coverage',
      price: 299.99,
      rating: 4.8,
      reviews: 323,
      image: 'https://via.placeholder.com/300x200',
      category: 'Networking',
      tags: ['Mesh', 'WiFi 6', 'Smart Home'],
      specs: {
        coverage: '5000 sq ft',
        speed: 'AX6000',
        ports: '3x Gigabit',
        features: 'Tri-band'
      }
    },

    // Additional products
    {
      id: 10,
      name: 'Ultra-Wide Gaming Monitor',
      description: '34" curved ultra-wide gaming monitor',
      price: 699.99,
      rating: 4.7,
      reviews: 245,
      image: 'https://via.placeholder.com/300x200',
      category: 'PC Hardware',
      tags: ['Gaming', 'Ultra-Wide', '144Hz'],
      specs: {
        size: '34" Curved',
        resolution: '3440x1440',
        refresh: '144Hz',
        response: '1ms'
      }
    },

    // PC Builds
    {
      id: 'pc-build-1',
      title: '600€ Gaming PC Build',
      description: 'Custom gaming PC build optimized for 1080p gaming with great price-to-performance ratio',
      price: 600,
      rating: 4.5,
      reviews: 123,
      image: 'https://www.pcworld.com/wp-content/uploads/2024/03/600-Euro-Gaming-PC-1.jpg?resize=1024%2C576&quality=50&strip=all',
      category: 'PC Builds',
      tags: ['Gaming', 'Budget', 'Custom Build'],
      specs: [
        '16GB DDR4 RAM',
        'AMD Ryzen 5',
        '500GB NVMe SSD',
        'GTX 1660 Super'
      ]
    },
  ];

  // Pagination calculations
  const indexOfLastProduct = page * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = products.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(products.length / productsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Enhanced Header */}
      <Box sx={{ bgcolor: '#000000', p: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Logo and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: 40, 
                  filter: 'brightness(0) invert(1)' 
                }} 
              />
              <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
                Marketplace
              </Typography>
            </Box>

            {/* Cart and Profile Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Cart Icon */}
              <Tooltip title="Shopping Cart">
                <IconButton 
                  onClick={handleCartMenuOpen}
                  sx={{ color: 'white' }}
                >
                  <Badge badgeContent={cartItems.length} color="error">
                    <ShoppingCartOutlined />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Profile Icon */}
              <Tooltip title="Account">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  sx={{ color: 'white' }}
                >
                  <PersonOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              gap: 1.5,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          My Profile
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <ListAlt fontSize="small" />
          </ListItemIcon>
          My Orders
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <FavoriteBorder fontSize="small" />
          </ListItemIcon>
          Wishlist
        </MenuItem>
        <Divider />
        <MenuItem>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Cart Menu */}
      <Menu
        anchorEl={cartAnchorEl}
        open={Boolean(cartAnchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 320,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {cartItems.length === 0 ? (
          <MenuItem>
            <Typography variant="body2" color="text.secondary">
              Your cart is empty
            </Typography>
          </MenuItem>
        ) : (
          <>
            {/* Cart items would go here */}
            <MenuItem>
              <Typography variant="body2">
                {cartItems.length} items in cart
              </Typography>
            </MenuItem>
          </>
        )}
        <Divider />
        <MenuItem>
          <Button
            fullWidth
            variant="contained"
            startIcon={<ShoppingCartOutlined />}
            disabled={cartItems.length === 0}
          >
            View Cart
          </Button>
        </MenuItem>
      </Menu>

      {/* Category Chips */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {categories.map((category) => (
            <Chip
              key={category.id}
              label={category.name}
              onClick={() => {/* Add category filter logic */}}
              sx={{ 
                '&:hover': { 
                  bgcolor: 'primary.main',
                  color: 'white'
                }
              }}
            />
          ))}
        </Box>
      </Container>

      {/* Search and Filter Section */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search products..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{ flexGrow: 1 }}
              >
                Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShoppingCart />}
                sx={{ flexGrow: 1 }}
              >
                Cart (0)
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Products Grid */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
        <Grid container spacing={3}>
          {currentProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease-in-out'
                }
              }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image}
                  alt={product.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Chip 
                    label={product.category}
                    size="small"
                    sx={{ mb: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography gutterBottom variant="h6" component="h2">
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {product.description}
                  </Typography>
                  
                  {/* Specs Summary */}
                  <Box sx={{ mb: 2, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                    {Array.isArray(product.specs) ? (
                      // If specs is an array
                      product.specs.map((spec, index) => (
                        <Typography key={index} variant="caption" display="block">
                          {spec}
                        </Typography>
                      ))
                    ) : (
                      // If specs is an object
                      Object.entries(product.specs || {}).map(([key, value]) => (
                        <Typography key={key} variant="caption" display="block">
                          <strong>{key}:</strong> {value}
                        </Typography>
                      ))
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    {product.tags && product.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Rating value={product.rating} precision={0.5} readOnly />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      ({product.reviews})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" color="primary">
                      ${product.price}
                    </Typography>
                    <Box>
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <Favorite />
                      </IconButton>
                      <IconButton size="small" sx={{ mr: 1 }}>
                        <Share />
                      </IconButton>
                      <Button variant="contained" startIcon={<ShoppingCart />}>
                        Add to Cart
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        <Box sx={{ 
          mt: 4, 
          display: 'flex', 
          justifyContent: 'center',
          pb: 4
        }}>
          <Pagination 
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                '&:hover': {
                  backgroundColor: 'primary.light',
                }
              }
            }}
          />
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default Marketplace; 